import { useEffect, useMemo, useRef, useState } from "react";
import type { KeyboardEvent as ReactKeyboardEvent } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/context/AuthContext";

// Normalized result contract returned by the canonical `global_search` RPC.
interface SearchHit {
  entity_type: string;
  entity_id: string;
  title: string;
  subtitle: string | null;
  matched_field: string | null;
  match_type: string; // exact | prefix | substring
  route: string;
  rank: number;
}

// Entity type → human label + icon.
const ENTITY_META: Record<string, { label: string; icon: string }> = {
  product: { label: "Product", icon: "ri-archive-2-line" },
  variant: { label: "Variant", icon: "ri-stack-line" },
  identifier: { label: "Identifier", icon: "ri-barcode-line" },
  supplier: { label: "Supplier", icon: "ri-truck-line" },
  supplier_sku: { label: "Supplier SKU", icon: "ri-price-tag-3-line" },
  customer: { label: "Customer", icon: "ri-user-3-line" },
  sales_order: { label: "Sales Order", icon: "ri-file-list-line" },
  purchase_order: { label: "Purchase Order", icon: "ri-shopping-cart-line" },
  shipment: { label: "Shipment", icon: "ri-box-3-line" },
  listing: { label: "Listing", icon: "ri-store-2-line" },
  external_reference: { label: "Channel Reference", icon: "ri-link" },
  stock_count: { label: "Stocktake", icon: "ri-clipboard-line" },
  return: { label: "Return", icon: "ri-arrow-go-back-line" },
  transfer: { label: "Transfer", icon: "ri-arrow-left-right-line" },
  location: { label: "Location", icon: "ri-map-pin-2-line" },
  exception: { label: "Exception", icon: "ri-alert-line" },
};

function fieldLabel(field: string | null): string {
  if (!field) return "";
  return field.replace(/_/g, " ");
}

function metaFor(type: string) {
  return ENTITY_META[type] ?? { label: type.replace(/_/g, " "), icon: "ri-search-line" };
}

export function GlobalSearch() {
  const { org } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [debounced, setDebounced] = useState("");
  const [active, setActive] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(query), 200);
    return () => clearTimeout(t);
  }, [query]);

  // Global ⌘K / Ctrl+K shortcut.
  useEffect(() => {
    function onKey(e: globalThis.KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((o) => !o);
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    if (open) {
      setActive(0);
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [open]);

  const q = debounced.trim();

  // One server-side request replaces the previous 14-query fan-out.
  const { data: results = [] } = useQuery({
    queryKey: ["global-search", org?.id, q],
    enabled: !!org?.id && q.length >= 1,
    queryFn: async () => {
      const { data, error } = await supabase.rpc("global_search", {
        p_query: q,
        p_org_id: org!.id,
        p_limit: 30,
      });
      if (error) throw error;
      return (data ?? []) as SearchHit[];
    },
  });

  const flat = useMemo(() => results, [results]);

  const select = (r: SearchHit) => {
    setOpen(false);
    setQuery("");
    navigate(r.route);
  };

  const trapTab = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    if (e.key !== "Tab" || !panelRef.current) return;
    const items = Array.from(
      panelRef.current.querySelectorAll<HTMLElement>('a[href], button:not([disabled]), input:not([disabled])'),
    );
    if (items.length === 0) return;
    const first = items[0];
    const last = items[items.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  };

  const onKeyDown = (e: ReactKeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((a) => Math.min(a + 1, flat.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((a) => Math.max(a - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (flat[active]) select(flat[active]);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="hidden md:flex items-center gap-2 w-72 px-3 py-2 text-sm bg-slate-50 border border-slate-200 rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-500 transition-all"
      >
        <i className="ri-search-line text-sm" />
        <span className="flex-1 text-left">Search anything...</span>
        <kbd className="text-[10px] font-medium text-slate-400 bg-white border border-slate-200 rounded px-1.5 py-0.5">⌘K</kbd>
      </button>

      {open && (
        <div className="fixed inset-0 z-50 flex items-start justify-center pt-[12vh] px-4">
          <div className="absolute inset-0 bg-slate-900/40" onClick={() => setOpen(false)} aria-hidden="true" />
          <div
            ref={panelRef}
            role="dialog"
            aria-modal="true"
            aria-label="Search"
            onKeyDown={trapTab}
            className="relative w-full max-w-xl bg-white rounded-xl border border-slate-200 shadow-2xl overflow-hidden"
          >
            <div className="flex items-center gap-3 px-4 border-b border-slate-100">
              <i className="ri-search-line text-slate-400 text-lg" aria-hidden="true" />
              <input
                ref={inputRef}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={onKeyDown}
                aria-label="Search products, orders, SKUs, tracking numbers, and customers"
                placeholder="Search products, SKUs, references, orders, tracking numbers, customers..."
                className="flex-1 py-3.5 text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/30 bg-transparent"
              />
              <button onClick={() => setOpen(false)} aria-label="Close search" className="w-7 h-7 flex items-center justify-center rounded-md hover:bg-slate-100 text-slate-400">
                <i className="ri-close-line" aria-hidden="true" />
              </button>
            </div>

            <div className="max-h-[50vh] overflow-y-auto py-2">
              {q.length === 0 && (
                <div className="px-4 py-8 text-center">
                  <p className="text-sm text-slate-400">Start typing to search across your business.</p>
                  <p className="text-xs text-slate-400 mt-1">Try a product name, SKU, reference, order number, or tracking number.</p>
                </div>
              )}
              {q.length > 0 && flat.length === 0 && (
                <div className="px-4 py-8 text-center text-sm text-slate-400">No results for "{debounced.trim()}".</div>
              )}
              {flat.map((r, idx) => {
                const meta = metaFor(r.entity_type);
                const isActive = idx === active;
                const isExact = r.match_type === "exact";
                return (
                  <button
                    key={`${r.entity_type}:${r.entity_id}:${r.matched_field}`}
                    onClick={() => select(r)}
                    onMouseEnter={() => setActive(idx)}
                    className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors ${isActive ? "bg-emerald-50" : ""}`}
                  >
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${isActive ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                      <i className={meta.icon} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-400 shrink-0">{meta.label}</span>
                        {r.matched_field && (
                          <span className="text-[10px] text-slate-400 truncate shrink">
                            · {fieldLabel(r.matched_field)}
                            {isExact ? " (exact)" : r.match_type === "prefix" ? " (prefix)" : ""}
                          </span>
                        )}
                      </div>
                      <p className="text-sm font-medium text-slate-800 truncate">{r.title}</p>
                      {r.subtitle && <p className="text-xs text-slate-400 truncate">{r.subtitle}</p>}
                    </div>
                    <i className="ri-arrow-right-s-line text-slate-300" />
                  </button>
                );
              })}
            </div>

            <div className="px-4 py-2.5 border-t border-slate-100 flex items-center gap-4 text-[10px] text-slate-400">
              <span className="flex items-center gap-1"><kbd className="px-1.5 py-0.5 bg-slate-100 border border-slate-200 rounded">↑↓</kbd> navigate</span>
              <span className="flex items-center gap-1"><kbd className="px-1.5 py-0.5 bg-slate-100 border border-slate-200 rounded">↵</kbd> open</span>
              <span className="flex items-center gap-1"><kbd className="px-1.5 py-0.5 bg-slate-100 border border-slate-200 rounded">esc</kbd> close</span>
            </div>
          </div>
        </div>
      )}
    </>
  );
}